import React, { useState, useEffect, useRef } from 'react';
import { CalendarDays, Plus, Camera, Image as ImageIcon, Loader2, Send, FileText } from 'lucide-react';
import { safeFetch, extractError } from '../../lib/safeFetch';
import { compressImage } from '../../lib/imageCompression';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const todayISO = () => new Date().toISOString().slice(0, 10);
const inclusiveDays = (from, to) => {
  if (!from || !to) return 0;
  const d1 = new Date(from);
  const d2 = new Date(to);
  if (isNaN(d1) || isNaN(d2) || d2 < d1) return 0;
  return Math.round((d2 - d1) / 86_400_000) + 1;
};

export default function LeaveForm() {
  const [showForm, setShowForm] = useState(false);
  const [days, setDays] = useState('');
  const [fromDate, setFromDate] = useState(todayISO());
  const [toDate, setToDate] = useState(todayISO());
  const [reason, setReason] = useState('');
  const [photo, setPhoto] = useState(null);
  const [busy, setBusy] = useState(false);
  const [list, setList] = useState([]);
  const [compressing, setCompressing] = useState(false);
  const fileRef = useRef(null);
  const galleryRef = useRef(null);

  // Auto-fill `days` when both dates are picked.
  useEffect(() => {
    const d = inclusiveDays(fromDate, toDate);
    if (d > 0) setDays(String(d));
  }, [fromDate, toDate]);

  const fetchList = async () => {
    const token = localStorage.getItem('token');
    const result = await safeFetch(`${API_URL}/api/leaves/my`, {
      headers: { 'Authorization': `Bearer ${token}` },
    });
    if (result.ok && Array.isArray(result.data)) setList(result.data);
  };

  useEffect(() => { fetchList(); }, []);

  const handlePhoto = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setCompressing(true);
    try {
      const compressed = await compressImage(f);
      setPhoto(compressed);
    } finally {
      setCompressing(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    if (!days || parseFloat(days) <= 0) { alert('Enter days'); return; }
    if (!fromDate || !toDate) { alert('Pick from and to dates'); return; }
    if (toDate < fromDate) { alert('To date must be on or after From date'); return; }
    setBusy(true);
    const token = localStorage.getItem('token');
    const result = await safeFetch(`${API_URL}/api/leaves`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({
        days: parseFloat(days),
        from_date: fromDate, to_date: toDate,
        reason: reason.trim(), photo_base64: photo,
      }),
    });
    if (result.ok) {
      alert('Leave sent to record. Your seniors have been notified.');
      setShowForm(false); setDays(''); setFromDate(todayISO()); setToDate(todayISO()); setReason(''); setPhoto(null);
      fetchList();
    } else {
      alert(extractError(result, 'Failed to submit leave'));
    }
    setBusy(false);
  };

  return (
    <div className="bg-white rounded-xl p-5 shadow-sm border" data-testid="leave-form">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center space-x-2">
          <CalendarDays className="text-rose-500" size={20} />
          <h3 className="font-semibold">Leaves</h3>
        </div>
        <button
          onClick={() => setShowForm(!showForm)}
          className="text-sm text-rose-600 flex items-center space-x-1"
          data-testid="leave-add-btn"
        >
          <Plus size={14} /><span>{showForm ? 'Close' : 'Apply Leave'}</span>
        </button>
      </div>

      {showForm && (
        <form onSubmit={submit} className="space-y-2 mb-4 p-3 bg-rose-50 rounded-lg">
          <div className="grid grid-cols-2 gap-2">
            <label className="text-xs">
              <span className="text-gray-600">From</span>
              <input
                type="date" value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                className="w-full mt-1 px-3 py-2 border rounded text-sm" required
                data-testid="leave-from-input"
              />
            </label>
            <label className="text-xs">
              <span className="text-gray-600">To</span>
              <input
                type="date" value={toDate} min={fromDate}
                onChange={(e) => setToDate(e.target.value)}
                className="w-full mt-1 px-3 py-2 border rounded text-sm" required
                data-testid="leave-to-input"
              />
            </label>
          </div>
          <input
            type="number" step="0.5" min="0.5" placeholder="Days"
            value={days} onChange={(e) => setDays(e.target.value)}
            className="w-full px-3 py-2 border rounded text-sm" required
            data-testid="leave-days-input"
          />
          <textarea
            placeholder="Reason (optional)" rows={2}
            value={reason} onChange={(e) => setReason(e.target.value)}
            className="w-full px-3 py-2 border rounded text-sm"
            data-testid="leave-reason-input"
          />
          <input
            ref={fileRef} type="file" accept="image/*" capture="environment"
            onChange={handlePhoto} className="hidden"
            data-testid="leave-photo-input"
          />
          <input
            ref={galleryRef} type="file" accept="image/*"
            onChange={handlePhoto} className="hidden"
            data-testid="leave-gallery-input"
          />
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button" disabled={compressing}
              onClick={() => { if (fileRef.current) { fileRef.current.value = ''; fileRef.current.click(); } }}
              className="px-3 py-2 border-2 border-dashed border-rose-300 rounded text-sm flex items-center justify-center gap-1 hover:bg-rose-50 disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="leave-photo-btn"
            >
              <Camera size={14} /><span>{photo ? 'Re-take' : 'Camera'}</span>
            </button>
            <button
              type="button" disabled={compressing}
              onClick={() => { if (galleryRef.current) { galleryRef.current.value = ''; galleryRef.current.click(); } }}
              className="px-3 py-2 border-2 border-dashed border-rose-300 rounded text-sm flex items-center justify-center gap-1 hover:bg-rose-50 disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="leave-gallery-btn"
            >
              <ImageIcon size={14} /><span>{photo ? 'Re-pick' : 'Gallery'}</span>
            </button>
          </div>
          {compressing && (
            <p className="text-xs text-rose-700 font-medium text-center flex items-center justify-center gap-1.5" data-testid="leave-compressing">
              <Loader2 size={12} className="animate-spin" /> Compressing…
            </p>
          )}
          {!compressing && photo && (
            <p className="text-xs text-emerald-700 font-medium text-center" data-testid="leave-photo-status">
              ✓ Leave form attached
            </p>
          )}
          <div className="flex space-x-2">
            <button
              type="submit" disabled={busy}
              className="flex-1 px-3 py-2 bg-rose-600 hover:bg-rose-700 text-white rounded text-sm font-semibold flex items-center justify-center gap-1 disabled:opacity-50"
              data-testid="leave-submit-btn"
            >
              <Send size={14} /> {busy ? 'Sending…' : 'Send to Record'}
            </button>
            <button
              type="button" onClick={() => setShowForm(false)}
              className="px-3 py-2 bg-gray-200 rounded text-sm"
              data-testid="leave-cancel-btn"
            >Cancel</button>
          </div>
        </form>
      )}

      <div className="space-y-2">
        {list.length === 0 && <p className="text-xs text-gray-500">No leaves recorded</p>}
        {list.map(lv => (
          <div key={lv.id} className="border rounded-lg p-3 text-sm" data-testid={`leave-row-${lv.id}`}>
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <p className="font-medium">
                  {lv.days} day(s) · {lv.from_date} → {lv.to_date}
                </p>
                {lv.reason && <p className="text-xs text-gray-500">{lv.reason}</p>}
                {lv.has_photo && (
                  <p className="text-[11px] text-gray-500 inline-flex items-center gap-1 mt-1">
                    <FileText size={10} /> Form attached
                  </p>
                )}
              </div>
              <span className={`px-2 py-1 text-xs rounded-full whitespace-nowrap ${
                lv.status === 'approved' ? 'bg-emerald-100 text-emerald-700' :
                lv.status === 'rejected' ? 'bg-red-100 text-red-700' :
                'bg-blue-100 text-blue-700'
              }`}>
                {lv.status === 'submitted' ? 'Recorded' : lv.status}
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
